import { Injectable } from '@angular/core';
import { InterpolatableTranslation, TranslateCompiler } from '@ngx-translate/core';
import { environment } from '../../../environment/environment';

/**
 * Custom TranslateCompiler for multi-tenant support.
 * Automatically replaces placeholders in translations with values from the environment configuration.
 *
 * Available placeholders:
 * - {{oem:brandName}} - The brand name
 * - {{oem:productName}} - The product name
 * - {{oem:companyName}} - The company name
 * - {{oem:supportEmail}} - The support email address
 * - {{oem:websiteUrl}} - The website URL
 *
 * Usage in translation files:
 * "text": "Contact {{oem:brandName}} support at {{oem:supportEmail}}"
 */
@Injectable()
export class TenantTranslateCompiler extends TranslateCompiler {
  private readonly placeholderPattern = /\{{oem:(\w+)\}\}/g;

  compile(value: string, lang: string): InterpolatableTranslation {
    return value;
  }

  compileTranslations(translations: any, lang: string): any {
    return this.processTranslations(translations);
  }

  /**
   * Recursively processes all translations and replaces placeholders
   */
  private processTranslations(obj: any): any {
    if (typeof obj === 'string') {
      return this.replacePlaceholders(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.processTranslations(item));
    }

    if (obj && typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          result[key] = this.processTranslations(obj[key]);
        }
      }
      return result;
    }

    return obj;
  }

  /**
   * Replaces placeholders in a string with corresponding values from the environment
   */
  private replacePlaceholders(text: string): string {
    return text.replace(this.placeholderPattern, (match, placeholder) => {
      const branding = environment.branding as any;

      if (branding && branding[placeholder] !== undefined) {
        return branding[placeholder];
      }

      if (!environment.production) {
        console.warn(`TenantTranslateCompiler: No value found for placeholder '${placeholder}'`);
      }
      return match;
    });
  }
}
